// src/components/NotificationToast.js
import React from 'react';
import { useNotification } from '../contexts/NotificationContext';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const NotificationToast = () => {
  const { notifications, removeNotification } = useNotification();

  // If no notifications, don't render anything
  if (notifications.length === 0) {
    return null;
  }

  // Get the icon based on notification type
  const getIcon = type => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="h-6 w-6 text-green-400" />;
      case 'error':
        return <ExclamationCircleIcon className="h-6 w-6 text-red-400" />;
      case 'warning':
        return <ExclamationCircleIcon className="h-6 w-6 text-yellow-400" />;
      case 'info':
      default:
        return <InformationCircleIcon className="h-6 w-6 text-blue-400" />;
    }
  };

  // Get background color based on notification type
  const getBackgroundColor = type => {
    switch (type) {
      case 'success':
        return 'bg-green-50';
      case 'error':
        return 'bg-red-50';
      case 'warning':
        return 'bg-yellow-50';
      case 'info':
      default:
        return 'bg-blue-50';
    }
  };

  // Get text color based on notification type
  const getTextColor = type => {
    switch (type) {
      case 'success':
        return 'text-green-800';
      case 'error':
        return 'text-red-800';
      case 'warning':
        return 'text-yellow-800';
      case 'info':
      default:
        return 'text-blue-800';
    }
  };

  return (
    <div className="fixed bottom-0 right-0 p-4 space-y-4 z-50">
      {notifications.map(notification => (
        <div
          key={notification.id}
          className={`max-w-sm rounded-lg shadow-lg ${getBackgroundColor(
            notification.type
          )} p-4 transition-all duration-300 ease-in-out transform translate-x-0`}
        >
          <div className="flex items-start">
            <div className="flex-shrink-0">{getIcon(notification.type)}</div>
            <div className={`ml-3 flex-1 ${getTextColor(notification.type)}`}>
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
            <div className="ml-4 flex-shrink-0 flex">
              <button
                onClick={() => removeNotification(notification.id)}
                className={`inline-flex rounded-md focus:outline-none ${getTextColor(
                  notification.type
                )}`}
              >
                <span className="sr-only">閉じる</span>
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default NotificationToast;
